import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Activity, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

const actionColors = {
  created_announcement: { bg: 'bg-blue-50', color: 'text-primary-blue', icon: '📣' },
  answered_doubt: { bg: 'bg-green-50', color: 'text-green-600', icon: '✅' },
  created_course: { bg: 'bg-yellow-50', color: 'text-yellow-600', icon: '📚' },
  deleted_user: { bg: 'bg-red-50', color: 'text-red-600', icon: '❌' },
  default: { bg: 'bg-gray-50', color: 'text-[#4D4D4D]', icon: '📄' }
};

export default function ActivityLog({ user, onLogout }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/activity-logs` 
        : `${API}/activity-logs?user_role=${filter}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setLogs(res.data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="activity-log"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="activity-log">
      <div className="p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-navy">📈 Activity Log</h1>
          <p className="text-[#4D4D4D] mt-2 font-light">Monitor all teacher and admin activities</p>
        </div>

        {/* Filter */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-primary-blue" strokeWidth={2} />
              <label className="font-semibold text-navy">Filter by Role:</label>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-48 h-10 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue">
                  <SelectValue placeholder="All Activities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Activities</SelectItem>
                  <SelectItem value="admin">Admin Only</SelectItem>
                  <SelectItem value="teacher">Teacher Only</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto text-sm text-[#4D4D4D]">
                {logs.length} log{logs.length !== 1 ? 's' : ''}
              </div>
            </div>
          </CardContent>
        </Card>

        {logs.length === 0 ? (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-12 text-center">
              <Activity className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
              <p className="text-xl font-bold text-navy">No activity logs yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => {
              const style = actionColors[log.action] || actionColors.default;
              return (
                <Card key={log.id} className="border-0 card-shadow bg-white rounded-xl card-hover transition-smooth">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${style.bg}`}>
                        <span className="text-2xl">{style.icon}</span>
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold ${style.color}`}>{log.action.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-[#4D4D4D]">
                          {log.user_name} ({log.user_role})
                        </p>
                        <p className="text-xs text-[#A5A5A5]">{formatDate(log.timestamp)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
